import { useState, useMemo } from 'react';
import {
  BarChart3,
  TrendingUp,
  PieChart as PieChartIcon,
  Users,
  Package,
  Calendar,
  DollarSign,
  ShoppingCart,
  AlertTriangle,
  Stethoscope,
  Syringe,
  Scissors,
  Bug,
  Ban,
  Wallet,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useDashboardStats, useGroomingCancellationsByOwner } from '@/hooks/useStats';
import { useExpenses } from '@/hooks/useFinance';
import { MONTH_NAMES } from '@/services/finance';
import Button from '@/components/ui/Button';

const MONTH_SHORT = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

// Colores para gráficos
const COLORS = ['#8b7355', '#a08060', '#b5906b', '#c9a076', '#deb082', '#d4a574', '#c9986a', '#be8b60'];
const PAYMENT_COLORS: Record<string, string> = {
  'cash': '#4ade80',
  'card': '#60a5fa',
  'transfer': '#a78bfa',
  'other': '#f472b6',
};

// Nombres amigables para métodos de pago
const PAYMENT_NAMES: Record<string, string> = {
  'cash': 'Efectivo',
  'card': 'Tarjeta',
  'transfer': 'Transferencia',
  'other': 'Otro',
};

// Nombres amigables para especies
const SPECIES_NAMES: Record<string, string> = {
  'perro': 'Perros',
  'gato': 'Gatos',
  'ave': 'Aves',
  'conejo': 'Conejos',
  'hamster': 'Hamsters',
  'otro': 'Otros',
};

type DatePreset = '7d' | '30d' | '90d' | '365d';

// Custom label renderer for pie charts
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const renderCustomLabel = (props: any) => {
  const { cx, cy, midAngle, outerRadius, name, percent } = props;
  const RADIAN = Math.PI / 180;
  const radius = outerRadius + 25;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  const pct = ((percent || 0) * 100).toFixed(0);

  return (
    <text
      x={x}
      y={y}
      fill="#4a4a4a"
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
      fontSize={11}
    >
      {`${name || ''} ${pct}%`}
    </text>
  );
};

export default function Reports() {
  const [datePreset, setDatePreset] = useState<DatePreset>('30d');

  const dateRange = useMemo(() => {
    const endDate = new Date();
    const startDate = new Date();

    switch (datePreset) {
      case '7d':
        startDate.setDate(startDate.getDate() - 7);
        break;
      case '30d':
        startDate.setDate(startDate.getDate() - 30);
        break;
      case '90d':
        startDate.setDate(startDate.getDate() - 90);
        break;
      case '365d':
        startDate.setDate(startDate.getDate() - 365);
        break;
    }

    return { startDate, endDate };
  }, [datePreset]);

  const { data: stats, isLoading, error } = useDashboardStats(dateRange);
  const { data: groomingCancellations } = useGroomingCancellationsByOwner();

  // === Gastos por categoría (mes a mes / total del año / histórico) ===
  const [expScope, setExpScope] = useState<'monthly' | 'year' | 'all'>('monthly');
  const [expYear, setExpYear] = useState(new Date().getFullYear());

  const { data: expenseRows = [], isLoading: loadingExpenseReport } = useExpenses(
    expScope === 'all' ? {} : { year: expYear }
  );

  // Matriz categoría x mes (los gastos fijos tienen month/year, no fecha)
  const expenseMatrix = useMemo(() => {
    const byCat = new Map<string, { name: string; months: number[]; total: number }>();
    for (const e of expenseRows) {
      const name = e.category?.name || 'Sin categoría';
      const row = byCat.get(name) || { name, months: Array(12).fill(0), total: 0 };
      const amount = Number(e.amount || 0);
      if (e.month >= 1 && e.month <= 12) row.months[e.month - 1] += amount;
      row.total += amount;
      byCat.set(name, row);
    }
    return Array.from(byCat.values()).sort((a, b) => b.total - a.total);
  }, [expenseRows]);

  const expenseMonthTotals = useMemo(() => {
    const totals = Array(12).fill(0);
    for (const r of expenseMatrix) r.months.forEach((v, i) => { totals[i] += v; });
    return totals;
  }, [expenseMatrix]);

  const expenseGrandTotal = useMemo(
    () => expenseMatrix.reduce((acc, r) => acc + r.total, 0),
    [expenseMatrix]
  );

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPeriod = (period: string) => {
    const date = new Date(period);
    if (datePreset === '7d' || datePreset === '30d') {
      return date.toLocaleDateString('es-AR', { day: '2-digit', month: 'short' });
    }
    return date.toLocaleDateString('es-AR', { month: 'short', year: '2-digit' });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-taupe-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
        Error al cargar las estadísticas. Por favor, intenta de nuevo.
      </div>
    );
  }

  if (!stats) return null;

  // Prepare data with labels for pie charts (con validación defensiva)
  const paymentMethodData = (stats.sales_by_payment_method || []).map(item => ({
    ...item,
    name: PAYMENT_NAMES[item.payment_method] || item.payment_method,
  }));

  const categoryData = (stats.sales_by_category || []).map(item => ({
    ...item,
    name: item.category_name,
  }));

  const speciesData = (stats.patients_by_species || []).map(item => ({
    ...item,
    name: SPECIES_NAMES[item.species?.toLowerCase()] || item.species,
  }));

  // Valores por defecto para evitar errores
  const salesSummary = stats.sales_summary || { total_revenue: 0, total_sales: 0, average_ticket: 0, total_items_sold: 0 };
  const servicesSummary = stats.services_summary || { total_consultations: 0, total_vaccinations: 0, total_dewormings: 0, total_grooming: 0 };
  const salesByPeriod = stats.sales_by_period || [];
  const topSellingProducts = stats.top_selling_products || [];
  const newPatientsByPeriod = stats.new_patients_by_period || [];
  const lowStockProducts = stats.low_stock_products || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <BarChart3 className="h-7 w-7 text-taupe-600" />
            Reportes y Estadísticas
          </h1>
          <p className="text-gray-500 mt-1">Análisis del rendimiento de tu veterinaria</p>
        </div>

        {/* Date Range Selector */}
        <div className="flex gap-2">
          {[
            { value: '7d', label: '7 días' },
            { value: '30d', label: '30 días' },
            { value: '90d', label: '90 días' },
            { value: '365d', label: '1 año' },
          ].map((preset) => (
            <Button
              key={preset.value}
              variant={datePreset === preset.value ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setDatePreset(preset.value as DatePreset)}
            >
              {preset.label}
            </Button>
          ))}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-green-100 rounded-lg">
              <DollarSign className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Ingresos Totales</p>
              <p className="text-xl font-semibold text-gray-900">
                {formatCurrency(salesSummary.total_revenue)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-blue-100 rounded-lg">
              <ShoppingCart className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Ventas</p>
              <p className="text-xl font-semibold text-gray-900">
                {salesSummary.total_sales}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-purple-100 rounded-lg">
              <TrendingUp className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Ticket Promedio</p>
              <p className="text-xl font-semibold text-gray-900">
                {formatCurrency(salesSummary.average_ticket)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-orange-100 rounded-lg">
              <Package className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Items Vendidos</p>
              <p className="text-xl font-semibold text-gray-900">
                {salesSummary.total_items_sold}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row 1: Sales by Period + Payment Methods */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sales by Period - Line Chart */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
            <Calendar className="h-5 w-5 text-taupe-600" />
            Ventas por Período
          </h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={salesByPeriod}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="period"
                  tickFormatter={formatPeriod}
                  tick={{ fontSize: 12 }}
                  stroke="#9ca3af"
                />
                <YAxis
                  tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`}
                  tick={{ fontSize: 12 }}
                  stroke="#9ca3af"
                />
                <Tooltip
                  formatter={(value) => [formatCurrency(Number(value)), 'Ingresos']}
                  labelFormatter={formatPeriod}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                />
                <Line
                  type="monotone"
                  dataKey="total_revenue"
                  stroke="#8b7355"
                  strokeWidth={2}
                  dot={{ fill: '#8b7355', strokeWidth: 2 }}
                  activeDot={{ r: 6, fill: '#8b7355' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Payment Methods - Pie Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
            <PieChartIcon className="h-5 w-5 text-taupe-600" />
            Métodos de Pago
          </h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={paymentMethodData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="total_revenue"
                  nameKey="name"
                  label={renderCustomLabel}
                  labelLine={{ stroke: '#9ca3af', strokeWidth: 1 }}
                >
                  {paymentMethodData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={PAYMENT_COLORS[entry.payment_method] || COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value) => formatCurrency(Number(value))}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Charts Row 2: Top Products + Categories */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Selling Products - Horizontal Bar */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
            <Package className="h-5 w-5 text-taupe-600" />
            Productos Más Vendidos
          </h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={topSellingProducts.slice(0, 8)}
                layout="vertical"
                margin={{ left: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                <XAxis
                  type="number"
                  tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`}
                  tick={{ fontSize: 12 }}
                  stroke="#9ca3af"
                />
                <YAxis
                  type="category"
                  dataKey="product_name"
                  width={160}
                  tick={{ fontSize: 11 }}
                  stroke="#9ca3af"
                  tickFormatter={(v) => v.length > 25 ? v.substring(0, 25) + '...' : v}
                />
                <Tooltip
                  formatter={(value, name) => [
                    name === 'total_revenue' ? formatCurrency(Number(value)) : value,
                    name === 'total_revenue' ? 'Ingresos' : 'Cantidad'
                  ]}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                />
                <Bar dataKey="total_revenue" fill="#8b7355" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Sales by Category - Pie Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
            <PieChartIcon className="h-5 w-5 text-taupe-600" />
            Ventas por Categoría
          </h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={categoryData}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="total_revenue"
                  nameKey="name"
                  label={renderCustomLabel}
                  labelLine={{ stroke: '#9ca3af', strokeWidth: 1 }}
                >
                  {categoryData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value) => formatCurrency(Number(value))}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Charts Row 3: Patients + Services */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* New Patients by Period */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
            <Users className="h-5 w-5 text-taupe-600" />
            Nuevos Pacientes
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={newPatientsByPeriod}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="period"
                  tickFormatter={formatPeriod}
                  tick={{ fontSize: 12 }}
                  stroke="#9ca3af"
                />
                <YAxis
                  tick={{ fontSize: 12 }}
                  stroke="#9ca3af"
                  allowDecimals={false}
                />
                <Tooltip
                  formatter={(value) => [value, 'Pacientes']}
                  labelFormatter={formatPeriod}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                />
                <Bar dataKey="count" fill="#60a5fa" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Patients by Species */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
            <PieChartIcon className="h-5 w-5 text-taupe-600" />
            Por Especie
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={speciesData}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={70}
                  paddingAngle={2}
                  dataKey="count"
                  nameKey="name"
                  label={renderCustomLabel}
                  labelLine={{ stroke: '#9ca3af', strokeWidth: 1 }}
                >
                  {speciesData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value) => [value, 'Pacientes']}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Services Summary Cards */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
          <Stethoscope className="h-5 w-5 text-taupe-600" />
          Resumen de Servicios
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 rounded-lg p-4 text-center">
            <Stethoscope className="h-8 w-8 text-blue-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-blue-700">{servicesSummary.total_consultations}</p>
            <p className="text-sm text-blue-600">Consultas</p>
          </div>
          <div className="bg-green-50 rounded-lg p-4 text-center">
            <Syringe className="h-8 w-8 text-green-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-green-700">{servicesSummary.total_vaccinations}</p>
            <p className="text-sm text-green-600">Vacunaciones</p>
          </div>
          <div className="bg-purple-50 rounded-lg p-4 text-center">
            <Bug className="h-8 w-8 text-purple-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-purple-700">{servicesSummary.total_dewormings}</p>
            <p className="text-sm text-purple-600">Desparasitaciones</p>
          </div>
          <div className="bg-pink-50 rounded-lg p-4 text-center">
            <Scissors className="h-8 w-8 text-pink-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-pink-700">{servicesSummary.total_grooming}</p>
            <p className="text-sm text-pink-600">Peluquería</p>
          </div>
        </div>
      </div>

      {/* Grooming Cancellations by Owner */}
      {groomingCancellations && groomingCancellations.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-red-200 p-5">
          <h3 className="text-lg font-medium text-red-800 mb-4 flex items-center gap-2">
            <Ban className="h-5 w-5 text-red-600" />
            Cancelaciones de Peluquería por Cliente ({groomingCancellations.length})
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="pb-2 font-medium">Cliente</th>
                  <th className="pb-2 font-medium">Teléfono</th>
                  <th className="pb-2 font-medium text-center">Cancelaciones</th>
                </tr>
              </thead>
              <tbody>
                {groomingCancellations.slice(0, 10).map((item) => (
                  <tr key={item.owner_id} className="border-b border-gray-50">
                    <td className="py-2 font-medium text-gray-900">{item.owner_name}</td>
                    <td className="py-2 text-gray-600">{item.owner_phone || '-'}</td>
                    <td className="py-2 text-center">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        {item.cancellation_count}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Low Stock Alert */}
      {lowStockProducts.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-amber-200 p-5">
          <h3 className="text-lg font-medium text-amber-800 mb-4 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            Productos con Stock Bajo ({lowStockProducts.length})
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="pb-2 font-medium">Producto</th>
                  <th className="pb-2 font-medium">Categoría</th>
                  <th className="pb-2 font-medium text-center">Stock Actual</th>
                  <th className="pb-2 font-medium text-center">Stock Mínimo</th>
                  <th className="pb-2 font-medium text-center">Estado</th>
                </tr>
              </thead>
              <tbody>
                {lowStockProducts.slice(0, 10).map((product) => (
                  <tr key={product.id} className="border-b border-gray-50">
                    <td className="py-2 font-medium text-gray-900">{product.name}</td>
                    <td className="py-2 text-gray-600">{product.category_name || '-'}</td>
                    <td className="py-2 text-center">
                      <span className={`font-medium ${product.stock === 0 ? 'text-red-600' : 'text-amber-600'}`}>
                        {product.stock}
                      </span>
                    </td>
                    <td className="py-2 text-center text-gray-600">{product.minimum_stock}</td>
                    <td className="py-2 text-center">
                      {product.stock === 0 ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Sin stock
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                          Bajo
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Gastos por Categoría */}
      <div className="bg-white rounded-xl shadow-sm border p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            <Wallet className="h-5 w-5 text-gray-500" />
            Gastos por Categoría
          </h3>

          <div className="flex flex-wrap items-center gap-2">
            {/* Navegación de año (no aplica al histórico) */}
            {expScope !== 'all' && (
              <div className="flex items-center gap-1 mr-1">
                <button
                  type="button"
                  onClick={() => setExpYear((y) => y - 1)}
                  className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50"
                  title="Año anterior"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="text-sm font-medium text-gray-700 w-12 text-center tabular-nums">
                  {expYear}
                </span>
                <button
                  type="button"
                  onClick={() => setExpYear((y) => y + 1)}
                  className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50"
                  title="Año siguiente"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}

            {([
              { key: 'monthly' as const, label: 'Mes a mes' },
              { key: 'year' as const, label: 'Total del año' },
              { key: 'all' as const, label: 'Histórico' },
            ]).map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setExpScope(opt.key)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                  expScope === opt.key
                    ? 'bg-gray-900 text-white border-gray-900'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {loadingExpenseReport ? (
          <p className="text-center py-10 text-gray-500">Cargando gastos...</p>
        ) : expenseMatrix.length === 0 ? (
          <p className="text-center py-10 text-gray-500">
            No hay gastos cargados {expScope === 'all' ? 'todavía' : `en ${expYear}`}.
          </p>
        ) : expScope === 'monthly' ? (
          /* --- MES A MES: matriz categoría x mes --- */
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="pb-2 pr-3 font-medium sticky left-0 bg-white">Categoría</th>
                  {MONTH_SHORT.map((m) => (
                    <th key={m} className="pb-2 px-2 font-medium text-right whitespace-nowrap">{m}</th>
                  ))}
                  <th className="pb-2 pl-3 font-medium text-right whitespace-nowrap">Total</th>
                </tr>
              </thead>
              <tbody>
                {expenseMatrix.map((row) => (
                  <tr key={row.name} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="py-2 pr-3 font-medium text-gray-900 whitespace-nowrap sticky left-0 bg-white">
                      {row.name}
                    </td>
                    {row.months.map((v, i) => (
                      <td
                        key={i}
                        className={`py-2 px-2 text-right tabular-nums whitespace-nowrap ${
                          v > 0 ? 'text-gray-700' : 'text-gray-300'
                        }`}
                      >
                        {v > 0 ? formatCurrency(v) : '—'}
                      </td>
                    ))}
                    <td className="py-2 pl-3 text-right font-semibold text-gray-900 tabular-nums whitespace-nowrap">
                      {formatCurrency(row.total)}
                    </td>
                  </tr>
                ))}
                <tr className="border-t-2 border-gray-200">
                  <td className="py-2 pr-3 font-semibold text-gray-900 sticky left-0 bg-white">Total</td>
                  {expenseMonthTotals.map((v, i) => (
                    <td key={i} className="py-2 px-2 text-right font-semibold text-gray-700 tabular-nums whitespace-nowrap">
                      {v > 0 ? formatCurrency(v) : '—'}
                    </td>
                  ))}
                  <td className="py-2 pl-3 text-right font-bold text-gray-900 tabular-nums whitespace-nowrap">
                    {formatCurrency(expenseGrandTotal)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          /* --- GENERAL: total por categoría --- */
          <div className="space-y-2">
            {expenseMatrix.map((row) => {
              const pct = expenseGrandTotal > 0 ? (row.total / expenseGrandTotal) * 100 : 0;
              return (
                <div key={row.name} className="flex items-center gap-3">
                  <div className="w-36 sm:w-48 flex-shrink-0">
                    <p className="text-sm font-medium text-gray-900 break-words">{row.name}</p>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                      <div
                        className="h-full bg-gray-700 rounded-full"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-semibold text-gray-900 tabular-nums whitespace-nowrap">
                      {formatCurrency(row.total)}
                    </p>
                    <p className="text-xs text-gray-400 tabular-nums">{pct.toFixed(1)}%</p>
                  </div>
                </div>
              );
            })}
            <div className="flex items-center justify-between border-t pt-3 mt-3">
              <span className="text-sm font-semibold text-gray-700">
                Total {expScope === 'all' ? '(histórico)' : expYear}
              </span>
              <span className="text-lg font-bold text-gray-900 tabular-nums">
                {formatCurrency(expenseGrandTotal)}
              </span>
            </div>
          </div>
        )}

        {expScope === 'monthly' && (
          <p className="text-xs text-gray-400 mt-3">
            Mostrando {MONTH_NAMES[0]}–{MONTH_NAMES[11]} de {expYear}. Deslizá para ver todos los meses.
          </p>
        )}
      </div>
    </div>
  );
}
